/**
 * User preferences utilities for saved deals and followed brands
 * Uses backend API for persistence
 */

import { userAPI } from "../services/api";
import logger from "./logger";

const SAVED_DEALS_KEY = "loops_saved_deals";
const FOLLOWED_BRANDS_KEY = "loops_followed_brands";

// Cache for saved deals (to avoid repeated API calls)
let savedDealsCache = null;
let savedDealsCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/** Single in-flight fetch so many list rows share one request */
let getSavedDealsInFlight = null;

// ===== Saved Deals =====

// Get saved deals from backend API
export const getSavedDeals = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    return [];
  }

  const now = Date.now();
  if (
    savedDealsCache &&
    savedDealsCacheTimestamp &&
    now - savedDealsCacheTimestamp < CACHE_DURATION
  ) {
    return savedDealsCache;
  }

  if (getSavedDealsInFlight) {
    return getSavedDealsInFlight;
  }

  getSavedDealsInFlight = (async () => {
    try {
      const response = await userAPI.getSavedDeals();
      const savedDealIds = response.data?.savedDealIds || [];

      savedDealsCache = savedDealIds;
      savedDealsCacheTimestamp = Date.now();

      return savedDealIds;
    } catch (error) {
      if (error.response?.status === 401) {
        return [];
      }
      logger.error("Error fetching saved deals:", error);
      return [];
    } finally {
      getSavedDealsInFlight = null;
    }
  })();

  return getSavedDealsInFlight;
};

// Get saved deals synchronously (for backward compatibility)
// Falls back to localStorage if API fails
export const getSavedDealsSync = () => {
  try {
    if (savedDealsCache) {
      return savedDealsCache;
    }
    // Fallback to localStorage
    const saved = localStorage.getItem(SAVED_DEALS_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (error) {
    logger.error("Error reading saved deals:", error);
    return [];
  }
};

// Save a deal via backend API
export const saveDeal = async (dealId) => {
  try {
    const response = await userAPI.saveDeal(dealId);
    const savedDealIds = response.data?.savedDealIds || [];

    // Update cache
    savedDealsCache = savedDealIds;
    savedDealsCacheTimestamp = Date.now();

    return true;
  } catch (error) {
    logger.error("Error saving deal:", error);
    return false;
  }
};

// Unsave a deal via backend API
export const unsaveDeal = async (dealId) => {
  try {
    const response = await userAPI.unsaveDeal(dealId);
    const savedDealIds = response.data?.savedDealIds || [];

    // Update cache
    savedDealsCache = savedDealIds;
    savedDealsCacheTimestamp = Date.now();

    return true;
  } catch (error) {
    logger.error("Error unsaving deal:", error);
    return false;
  }
};

// Check if deal is saved (synchronous, uses cache)
export const isDealSaved = (dealId) => {
  const saved = getSavedDealsSync();
  return saved.includes(dealId);
};

// Clear cache (called on logout)
export const clearSavedDealsCache = () => {
  savedDealsCache = null;
  savedDealsCacheTimestamp = null;
  getSavedDealsInFlight = null;
};

/** Align in-memory saved IDs with a fresh server list (e.g. after React Query savedDeals fetch) */
export const replaceSavedDealsCache = (savedDealIds) => {
  savedDealsCache = Array.isArray(savedDealIds) ? [...savedDealIds] : [];
  savedDealsCacheTimestamp = Date.now();
};

// Clean up invalid deal IDs (no longer needed with backend, but kept for migration)
export const cleanupInvalidDealIds = () => {
  // This is now handled by the backend
  return 0;
};

// ===== Followed Brands =====

// Cache for followed brands (to avoid repeated API calls)
let followedBrandsCache = null;
let followedBrandsCacheTimestamp = null;

/** Rows from POST /brands/batch — keyed by sorted id string, same TTL as followed IDs */
let followedBrandsBatchCacheRows = null;
let followedBrandsBatchCacheIdsKey = "";
let followedBrandsBatchCacheAt = 0;

/**
 * API once returned populated brand docs; batch endpoints need string ids.
 * Dedupes while preserving order.
 */
export const normalizeFollowedBrandIdsList = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    if (item == null) continue;
    let s = null;
    if (typeof item === "object" && item._id != null) {
      s = String(item._id);
    } else if (typeof item === "string" || typeof item === "number") {
      s = String(item);
    } else if (item?.toString && typeof item.toString === "function") {
      const t = item.toString();
      if (t && t !== "[object Object]") s = t;
    }
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
};

const followedBrandIdsBatchCacheKey = (ids) =>
  normalizeFollowedBrandIdsList(ids)
    .slice()
    .sort()
    .join("\0");

/**
 * Cached brand docs for `/brands/batch` — same TTL as {@link getFollowedBrands} (unless forceRefresh).
 */
export const getCachedFollowedBrandsBatchRows = (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return null;
  const key = followedBrandIdsBatchCacheKey(ids);
  const now = Date.now();
  if (
    followedBrandsBatchCacheRows &&
    followedBrandsBatchCacheIdsKey === key &&
    now - followedBrandsBatchCacheAt < CACHE_DURATION
  ) {
    return followedBrandsBatchCacheRows;
  }
  return null;
};

export const setCachedFollowedBrandsBatchRows = (ids, rows) => {
  if (!Array.isArray(ids) || ids.length === 0) {
    followedBrandsBatchCacheRows = null;
    followedBrandsBatchCacheIdsKey = "";
    followedBrandsBatchCacheAt = 0;
    return;
  }
  followedBrandsBatchCacheIdsKey = followedBrandIdsBatchCacheKey(ids);
  followedBrandsBatchCacheRows = Array.isArray(rows) ? rows : [];
  followedBrandsBatchCacheAt = Date.now();
};

/**
 * @param {{ forceRefresh?: boolean }} [options] — If true, skip cache and hit the API (rare: force sync with server).
 */
export const getFollowedBrands = async (options = {}) => {
  const forceRefresh = options.forceRefresh === true;
  try {
    // Check if user is authenticated (has token)
    const token = localStorage.getItem("token");
    if (!token) {
      // Not authenticated, return empty array
      return [];
    }

    // Check cache first
    const now = Date.now();
    if (
      !forceRefresh &&
      followedBrandsCache &&
      followedBrandsCacheTimestamp &&
      now - followedBrandsCacheTimestamp < CACHE_DURATION
    ) {
      return normalizeFollowedBrandIdsList(followedBrandsCache);
    }

    const response = await userAPI.getFollowedBrands();
    const followedBrandIds = normalizeFollowedBrandIdsList(
      response.data?.followedBrandIds || [],
    );

    // Update cache
    followedBrandsCache = followedBrandIds;
    followedBrandsCacheTimestamp = now;

    return followedBrandIds;
  } catch (error) {
    // If 401, user is not authenticated - return empty array silently
    if (error.response?.status === 401) {
      return [];
    }
    logger.error("Error fetching followed brands:", error);
    // Return empty array on error
    return [];
  }
};

// Get followed brands synchronously (for backward compatibility)
// Falls back to localStorage if API fails
export const getFollowedBrandsSync = () => {
  try {
    if (followedBrandsCache) {
      return normalizeFollowedBrandIdsList(followedBrandsCache);
    }
    // Fallback to localStorage
    const followed = localStorage.getItem(FOLLOWED_BRANDS_KEY);
    const parsed = followed ? JSON.parse(followed) : [];
    return normalizeFollowedBrandIdsList(parsed);
  } catch (error) {
    logger.error("Error reading followed brands:", error);
    return [];
  }
};

const normalizeBrandId = (brandId) => {
  if (brandId == null) return null;
  if (typeof brandId === "object" && brandId._id != null) {
    return String(brandId._id);
  }
  return String(brandId);
};

// Follow a brand via backend API
/** @param {{ quiet?: boolean }} [options] — If `quiet`, skip UI broadcast events (batch callers flush once). */
export const followBrand = async (brandId, options = {}) => {
  const quiet = options.quiet === true;
  const id = normalizeBrandId(brandId);
  if (!id) return false;
  try {
    const response = await userAPI.followBrand(id);
    const followedBrandIds = normalizeFollowedBrandIdsList(
      response.data?.followedBrandIds || [],
    );

    // Update cache
    followedBrandsCache = followedBrandIds;
    followedBrandsCacheTimestamp = Date.now();

    if (!quiet) {
      window.dispatchEvent(new Event("followedBrandsChanged"));
    }

    return true;
  } catch (error) {
    logger.error("Error following brand:", error);
    return false;
  }
};

// Unfollow a brand via backend API
/** @param {{ quiet?: boolean }} [options] — If `quiet`, skip UI broadcast events (batch callers flush once). */
export const unfollowBrand = async (brandId, options = {}) => {
  const quiet = options.quiet === true;
  const id = normalizeBrandId(brandId);
  if (!id) return false;
  try {
    const response = await userAPI.unfollowBrand(id);
    const followedBrandIds = normalizeFollowedBrandIdsList(
      response.data?.followedBrandIds || [],
    );

    // Update cache
    followedBrandsCache = followedBrandIds;
    followedBrandsCacheTimestamp = Date.now();

    if (!quiet) {
      window.dispatchEvent(new Event("followedBrandsChanged"));
    }

    return true;
  } catch (error) {
    logger.error("Error unfollowing brand:", error);
    return false;
  }
};

// Check if brand is followed (synchronous, uses cache)
export const isBrandFollowed = (brandId) => {
  const followed = getFollowedBrandsSync();
  // Normalize brandId to string
  const brandIdStr = brandId?.toString ? brandId.toString() : brandId;

  // Handle both ObjectId strings, Brand objects (from populated query), and regular strings
  return followed.some((id) => {
    // Handle populated Brand objects (have _id property)
    if (id && typeof id === "object" && id._id) {
      const idStr = id._id?.toString ? id._id.toString() : id._id;
      return idStr === brandIdStr;
    }
    // Handle ObjectId instances
    if (id?.toString) {
      return id.toString() === brandIdStr;
    }
    // Handle strings
    return id === brandIdStr;
  });
};

// Clear followed brands cache (called on logout)
export const clearFollowedBrandsCache = () => {
  followedBrandsCache = null;
  followedBrandsCacheTimestamp = null;
  followedBrandsBatchCacheRows = null;
  followedBrandsBatchCacheIdsKey = "";
  followedBrandsBatchCacheAt = 0;
};

// Clear all saved deals and followed brands (called on logout)
export const clearUserPreferences = () => {
  try {
    // Clear caches
    clearSavedDealsCache();
    clearFollowedBrandsCache();
    // Clear localStorage (for migration/fallback)
    localStorage.removeItem(SAVED_DEALS_KEY);
    localStorage.removeItem(FOLLOWED_BRANDS_KEY);
    return true;
  } catch (error) {
    logger.error("Error clearing user preferences:", error);
    return false;
  }
};
